<template>
    <div class="code-verify">
        <PageTitleBanner showBack/>
        <div class="verify-title" v-html="replaceTranslateContent($t('m.verifyTitle'))"></div>
        <VerificationCode class="verify-input" type="number" :maxlength="maxlength" @output="codeOutput"></VerificationCode>
        <div class="sms-timer" v-show="reSendTime >0">{{ $t('m.resend') + ' (' + reSendTime + 's)' }}</div>
        <div v-if="useTurnstile" ref="turnstileContainer" class="turnstile-container" :class="{ interactive: showTurnstileOverlay }"></div>
        <vue-recaptcha v-if="useRecaptcha" ref="resend-recaptcha" size="invisible" :sitekey="captchaSiteKey" :loadRecaptchaScript="true" :recaptchaHost="recaptchaHost" @verify="getVerifyCode" @error="onRecaptchaError"/>
        <span v-active-touch class="resend-btn interactive-text" @click="reSendSms" v-show="reSendTime === 0">{{ $t('m.resend') }}</span>
        <span v-active-touch @click="toLogin" class="primary-btn verify-btn" :class="code && code.length === maxlength ? '' : 'invalid'">{{ $t('next') }}</span>
        <div v-if="showTurnstileOverlay" class="turnstile-overlay"></div>
        <LanguageSelector/>
    </div>
</template>

<script>
import {getVerifyCode, loginOrRegister} from "../../api/notLogin";
import PageTitleBanner from "../../components/PageTitleBanner";
import VerificationCode from "../../components/forms/VerificationCode";
import LanguageSelector from '../../components/LanguageSelector';
import VueRecaptcha from 'vue-recaptcha';
import {getRecaptchaHost} from "@/utils/tool";
import { getRouteAfterLogin } from "@/utils/postLoginRedirect";
import { ensureTurnstileLoaded, getTurnstileLanguage } from "@/utils/turnstile";
import { mapGetters } from 'vuex';

export default {
    name: "CodeVerify",
    components: {VerificationCode, PageTitleBanner, VueRecaptcha, LanguageSelector},
    data() {
        return {
            tel: this.$route.params.tel || '',
            tel_code: this.$route.params.tel_code || '',
            maxlength: 6,
            code: undefined,
            reSendTime: 60,
            setInterval: undefined,
            recaptchaHost: getRecaptchaHost(),
            debounceTimer: null, // 输入验证码防抖
            loginAttempts: 0, // 记录自动请求登录次数,
            turnstileWidgetId: undefined,
            showTurnstileOverlay: false,
        };
    },
    computed: {
        ...mapGetters('appConfig', {
            captchaEnabled: 'captcha_enabled',
            captchaProvider: 'captcha_provider',
            captchaSiteKey: 'captcha_site_key',
        }),
        useTurnstile() {
            return !!this.captchaEnabled && this.captchaProvider === 'turnstile'
        },
        useRecaptcha() {
            return !!this.captchaEnabled && this.captchaProvider === 'recaptcha'
        },
    },
    created() {
        this.setInterval = setInterval(this.timerHandler, 1000)
    },
    mounted() {
        this.initTurnstile()
    },
    watch: {
        code() {
            if(this.code.length === this.maxlength && this.loginAttempts < 3) {
                // 设置0.3秒防抖，前三次自动请求
                clearTimeout(this.debounceTimer);
                    this.debounceTimer = setTimeout(() => {
                    this.toLogin();
                }, 300);
            }
        },
        '$i18n.locale'() {
            if (this.useTurnstile) {
                this.initTurnstile()
            }
        },
        useTurnstile() {
            this.initTurnstile()
        },
        captchaSiteKey() {
            this.initTurnstile()
        }
    },
    beforeDestroy() {
        // 清除定时器
        clearTimeout(this.debounceTimer);
        this.removeTurnstile()
    },
    methods: {
        removeTurnstile() {
            if (this.turnstileWidgetId !== undefined && window.turnstile && typeof window.turnstile.remove === 'function') {
                window.turnstile.remove(this.turnstileWidgetId)
            }
            this.turnstileWidgetId = undefined
        },
        async initTurnstile() {
            if (!this.useTurnstile || !this.captchaSiteKey) {
                this.removeTurnstile()
                return
            }

            await this.$nextTick()

            if (!this.$refs.turnstileContainer) {
                return
            }

            try {
                const turnstile = await ensureTurnstileLoaded(this.$loadScript)
                this.removeTurnstile()
                this.turnstileWidgetId = turnstile.render(this.$refs.turnstileContainer, {
                    sitekey: this.captchaSiteKey,
                    size: 'flexible',
                    execution: 'execute',
                    appearance: 'interaction-only',
                    language: getTurnstileLanguage(this.$i18n && this.$i18n.locale),
                    'before-interactive-callback': () => {
                        this.hideLoading()
                        this.showTurnstileOverlay = true
                    },
                    callback: token => this.getVerifyCode(token),
                    'error-callback': () => this.onTurnstileError(),
                    'expired-callback': () => this.onTurnstileExpired(),
                    'timeout-callback': () => this.onTurnstileError(),
                    theme: 'light',
                    action: 'resend_sms',
                })
            } catch (error) {
                this.turnstileWidgetId = undefined
            }
        },
        executeTurnstile() {
            const executeWidget = () => {
                if (window.turnstile && typeof window.turnstile.execute === 'function' && this.turnstileWidgetId !== undefined) {
                    window.turnstile.execute(this.turnstileWidgetId)
                    return
                }
                this.onTurnstileError()
            }

            if (!window.turnstile || typeof window.turnstile.execute !== 'function' || this.turnstileWidgetId === undefined) {
                this.initTurnstile().then(() => {
                    executeWidget()
                }).catch(() => {
                    this.onTurnstileError()
                })
                return
            }

            executeWidget()
        },
        resetTurnstile() {
            this.showTurnstileOverlay = false
            if (window.turnstile && typeof window.turnstile.reset === 'function' && this.turnstileWidgetId !== undefined) {
                window.turnstile.reset(this.turnstileWidgetId)
            }
        },
        onTurnstileError() {
            this.showTurnstileOverlay = false
            this.hideLoading()
            this.toast(this.$t('eMsg.30001'))
        },
        onTurnstileExpired() {
            this.resetTurnstile()
        },
        timerHandler() {
            this.reSendTime--
            if (this.reSendTime === 0) {
                this.setInterval && clearInterval(this.setInterval)
            }
        },
        toLogin() {
            if(this.code.length < this.maxlength) return
            this.loginAttempts++
            this.showLoading();
            const box_number = this.$store.getters['appConfig/device_code'];
            loginOrRegister({
                type:'tel', tel: this.tel, 
                tel_code: this.tel_code, 
                code: this.code, 
                box_number
            }).then(res => {
                console.log(res, 'login_res');
                this.$store.dispatch('setToken', res.data.token)
                let uInfoCollection = res.data.user_info_collection,
                    isBorrow = parseInt(res.data.is_borrow)
                this.$store.dispatch('setUserInfoCollection', uInfoCollection)
                this.$store.dispatch('setIsBorrow', isBorrow)
                this.$router.push(getRouteAfterLogin(uInfoCollection))
            }).catch(er => {
                this.toast(this.$t('eMsg.' + er.code))
            }).finally(_ => {
                this.hideLoading();
            });
        },
        codeOutput({data}) {
            this.code = data
        },
        reSendSms() {
            // 如果需要人机验证，调用人机验证，否则直接请求提交
            if (this.useTurnstile) {
                this.showLoading()
                this.executeTurnstile()
            } else if (this.useRecaptcha) {
                this.$refs["resend-recaptcha"].execute()
            } else {
                this.getVerifyCode()
            }
        },
        getVerifyCode(verify_token) {
            this.showLoading()
            this.showTurnstileOverlay = false
            const captcha_provider = this.captchaEnabled ? this.captchaProvider : undefined
            getVerifyCode({tel: this.tel, tel_code: this.tel_code, verify_token, captcha_provider}).then(r => {
                this.toast(this.$t('tMsg.sendSuccess'))
                this.reSendTime = 60
                this.setInterval = setInterval(this.timerHandler, 1000)
            }).catch(er => {
                this.toast(this.$t('eMsg.' + er.code))
            }).finally(_ => {
                this.hideLoading()
                this.resetTurnstile()
                this.$refs["resend-recaptcha"] && this.$refs["resend-recaptcha"].reset()
            })
        },
        onRecaptchaError() {
            this.hideLoading()
            this.toast(this.$t('eMsg.30001'))
        },
    }
};
</script>

<style lang="scss">
.code-verify {
    text-align: center;
}
.verify-title {
    width: 295px;
    margin: 34px auto 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    text-align: center;
    color: #202124;
}

.verify-input {
    margin-bottom: 12px;
}


.resend-btn,
.sms-timer {
    display: inline-block;
    color: #999999;
    margin: 25px auto;
}

.resend-btn {
    text-decoration: underline;
    color: var(--theme-selectable-accent-color, #276CE1);
}

.verify-btn.invalid {
    background: #E3E3E3;
    color: #A9A9A9;
    pointer-events: none;
}

.turnstile-container {
    position: fixed;
    width: 300px;
    height: 60px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10000;
    pointer-events: none;

    &.interactive {
        pointer-events: auto;
    }
}

.turnstile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(2px);
    z-index: 9999;
}

</style>
